import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Text, NumberInput, Flex, Box, UnstyledButton } from '@mantine/core';
import clsx from 'clsx';
import { getNumberInputValue } from './helpers';
import { CellUpdate } from './types';
import formatCurrency from '../../utility/formatCurrency';
import { PropagationStopper } from '../../components/common';

export interface CompetitorPriceCellProps {
    priceId?: string;
    price: number | null;
    isNA: boolean;
    currency: string;
    isEditable?: boolean;
    isSaving?: boolean;
    updateCell?: (priceId: string, update: CellUpdate) => void;
}

function CompetitorPriceCellInner({
    priceId,
    price,
    isNA,
    currency,
    isEditable = false,
    isSaving = false,
    updateCell,
}: CompetitorPriceCellProps) {
    const numberInputRef = useRef<HTMLInputElement>(null);
    const currencyCode = currency.trim().toUpperCase() || 'ZAR';

    const externalInputValue = getNumberInputValue(price, isNA);
    const [isEditing, setIsEditing] = useState(false);
    const [localValue, setLocalValue] = useState<string | number>(
        externalInputValue ?? '',
    );

    useEffect(() => {
        if (!isEditing) {
            setLocalValue(externalInputValue ?? '');
        }
    }, [externalInputValue, isEditing]);

    const commitPrice = useCallback(
        (value: string | number) => {
            if (isSaving || !updateCell || !priceId) {
                return;
            }
            const numValue =
                typeof value === 'number' ? value : parseFloat(value as string);
            const resolvedPrice = Number.isNaN(numValue) ? null : numValue;
            if (resolvedPrice === price) {
                return;
            }
            updateCell(priceId, { price: resolvedPrice });
        },
        [isSaving, updateCell, priceId, price],
    );

    const handleCellPriceChange = useCallback(
        (value: string | number) => {
            if (!isSaving) {
                setLocalValue(value);
            }
        },
        [isSaving],
    );

    const handleFocus = useCallback(() => {
        setIsEditing(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsEditing(false);
        commitPrice(localValue);
    }, [commitPrice, localValue]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                e.currentTarget.blur();
            }
        },
        [],
    );

    const cellOuterClasses = 'w-full h-full min-h-10 flex items-stretch';
    const cellInnerClasses = clsx(
        'w-full min-w-0 flex items-center pl-2 pr-4 py-1 border border-transparent transition-colors',
        isNA && 'bg-gray-100',
    );

    if (!isEditable || !updateCell || !priceId) {
        let displayText = `${currencyCode} —`;
        if (!isNA && price !== null && price !== undefined) {
            displayText = formatCurrency(price, currency);
        }

        return (
            <Box className={cellOuterClasses}>
                <Box className={cellInnerClasses}>
                    <Text size="sm" c="dark.6" className="text-sm">
                        {displayText}
                    </Text>
                </Box>
            </Box>
        );
    }

    const displayValue = isEditing ? localValue : externalInputValue ?? '';
    const isPriceUnset =
        !isNA &&
        (price === null || price === undefined) &&
        externalInputValue === undefined;

    const handleToggleNA = (e: React.MouseEvent | React.KeyboardEvent) => {
        if ('key' in e && !(e.key === 'Enter' || e.key === ' ')) {
            return;
        }

        if ('key' in e) {
            e.preventDefault();
        }

        if (!isSaving) {
            updateCell(priceId, { isNA: !isNA });
        }
    };

    return (
        <Box className={cellOuterClasses}>
            <Box
                className={clsx(
                    cellInnerClasses,
                    'flex-1 flex flex-col justify-center',
                )}
            >
                <Flex
                    align="center"
                    gap={0}
                    wrap="nowrap"
                    className="w-full min-w-0"
                >
                    <Box
                        className="flex-1 min-w-0 self-stretch flex items-center min-h-10 cursor-text"
                        onMouseDown={e => {
                            if (isNA || isSaving) {
                                return;
                            }
                            const input = numberInputRef.current;
                            if (!input) {
                                return;
                            }
                            if (!input.contains(e.target as Node)) {
                                input.focus();
                            }
                        }}
                    >
                        <NumberInput
                            ref={numberInputRef}
                            key={`${priceId}_${isNA}`}
                            value={isNA ? 0 : displayValue}
                            onChange={handleCellPriceChange}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            placeholder={
                                isPriceUnset ? `${currencyCode} —` : '—'
                            }
                            min={0}
                            decimalScale={2}
                            fixedDecimalScale={isNA}
                            thousandSeparator=""
                            prefix={
                                isPriceUnset ? undefined : `${currencyCode} `
                            }
                            hideControls
                            disabled={isNA || isSaving}
                            allowDecimal
                            variant="unstyled"
                            classNames={{
                                root: 'flex-1 min-w-0 w-full self-stretch flex items-center',
                                input: clsx(
                                    'text-sm w-full h-10 px-0 py-0 border-0 bg-transparent shadow-none transition-colors',
                                    'placeholder:text-gray-300',
                                    'focus:outline-none focus:ring-0',
                                    'disabled:bg-transparent disabled:opacity-100',
                                    isNA && 'text-gray-300 font-normal',
                                    !isNA && 'text-gray-800 font-medium',
                                ),
                                controls: 'hidden',
                            }}
                        />
                    </Box>
                    <Box className="ml-1.5 shrink-0">
                        <PropagationStopper>
                            <UnstyledButton
                                type="button"
                                onClick={handleToggleNA}
                                onKeyDown={handleToggleNA}
                                tabIndex={isSaving ? -1 : 0}
                                aria-label={isNA ? 'Remove N/A' : 'Mark as N/A'}
                                disabled={isSaving}
                                className={clsx(
                                    'shrink-0 flex items-center justify-center h-7 px-2.5 text-[11px] font-semibold rounded border border-solid shadow-none outline-none cursor-pointer',
                                    'focus-visible:ring-1 focus-visible:ring-gray-300 focus-visible:ring-offset-0',
                                    isSaving && 'cursor-not-allowed opacity-50',
                                    isNA
                                        ? 'text-red-600 border-red-300 bg-red-50'
                                        : 'text-gray-400 border-gray-200 bg-white',
                                )}
                            >
                                N/A
                            </UnstyledButton>
                        </PropagationStopper>
                    </Box>
                </Flex>
            </Box>
        </Box>
    );
}

export const CompetitorPriceCell = React.memo(CompetitorPriceCellInner);
CompetitorPriceCell.displayName = 'CompetitorPriceCell';
